import { apiClient } from "@/api/client";
import { create } from "zustand";

export interface CanvasCommentAuthor {
  id: string;
  name: string;
  email: string;
}

export interface CanvasCommentReply {
  id: string;
  comment: string;
  author: CanvasCommentAuthor;
  likes_count: number;
  liked_by_me: boolean;
  liked_by: { id: string; name: string }[];
  created_at: string;
}

export interface CanvasComment {
  id: string;
  x_position: string;
  y_position: string;
  interface_id: string;
  canvas_component_info_id: string | null;
  comment: string;
  is_resolved: boolean;
  resolved_by: { id: string; name: string } | null;
  resolved_at: string | null;
  author: CanvasCommentAuthor;
  likes_count: number;
  liked_by_me: boolean;
  liked_by: { id: string; name: string }[];
  replies_count: number;
  replies: CanvasCommentReply[];
  created_at: string;
}

interface CanvasCommentsStore {
  comments: CanvasComment[];
  isLoading: boolean;
  isSaving: boolean;
  activeCommentId: string | null;
  isDrawerOpen: boolean;
  pendingPin: { x: number; y: number } | null;

  fetchComments: (versionId: string) => Promise<void>;
  addComment: (versionId: string, payload: {
    comment: string;
    x_position: number;
    y_position: number;
    interface_id: string;
    canvas_component_info_id?: string | null;
  }) => Promise<CanvasComment>;
  addReply: (versionId: string, parentId: string, comment: string) => Promise<CanvasCommentReply>;
  toggleLike: (commentId: string) => Promise<void>;
  toggleResolve: (commentId: string) => Promise<void>;
  deleteComment: (commentId: string) => Promise<void>;

  openDrawer: (commentId: string | null) => void;
  closeDrawer: () => void;
  setPendingPin: (pin: { x: number; y: number } | null) => void;
  setActiveCommentId: (id: string | null) => void;
  reset: () => void;
}

const useCanvasCommentsStore = create<CanvasCommentsStore>((set, get) => ({
  comments: [],
  isLoading: false,
  isSaving: false,
  activeCommentId: null,
  isDrawerOpen: false,
  pendingPin: null,

  fetchComments: async (versionId: string) => {
    set({ isLoading: true });
    try {
      const res = await apiClient.get(`/asset-versions/${versionId}/canvas-comments`);
      set({ comments: res.data.data.comments ?? [], isLoading: false });
    } catch {
      set({ isLoading: false });
    }
  },

  addComment: async (versionId, payload) => {
    set({ isSaving: true });
    try {
      const res = await apiClient.post(`/asset-versions/${versionId}/canvas-comments`, payload);
      const rawAuthor = res.data.data.author;
      const newComment: CanvasComment = {
        ...res.data.data,
        author: rawAuthor
          ? {
              ...rawAuthor,
              name:
                rawAuthor.name ||
                `${rawAuthor.first_name ?? ""} ${rawAuthor.last_name ?? ""}`.trim(),
            }
          : { id: "", name: "", email: "" },
        likes_count: 0,
        liked_by_me: false,
        liked_by: [],
        replies_count: 0,
        replies: [],
      };
      set((state) => ({ comments: [...state.comments, newComment], isSaving: false }));
      return newComment;
    } catch (e) {
      set({ isSaving: false });
      throw e;
    }
  },

  addReply: async (versionId, parentId, comment) => {
    set({ isSaving: true });
    try {
      const res = await apiClient.post(`/asset-versions/${versionId}/canvas-comments`, {
        comment,
        parent_id: parentId,
      });
      const rawReplyAuthor = res.data.data.author;
      const reply: CanvasCommentReply = {
        ...res.data.data,
        author: rawReplyAuthor
          ? {
              ...rawReplyAuthor,
              name:
                rawReplyAuthor.name ||
                `${rawReplyAuthor.first_name ?? ""} ${rawReplyAuthor.last_name ?? ""}`.trim(),
            }
          : { id: "", name: "", email: "" },
        likes_count: 0,
        liked_by_me: false,
        liked_by: [],
      };
      set((state) => ({
        isSaving: false,
        comments: state.comments.map((c) =>
          c.id === parentId
            ? { ...c, replies: [...c.replies, reply], replies_count: c.replies_count + 1 }
            : c
        ),
      }));
      return reply;
    } catch (e) {
      set({ isSaving: false });
      throw e;
    }
  },

  toggleLike: async (commentId) => {
    try {
      const res = await apiClient.post(`/canvas-comments/${commentId}/like`);
      const { liked, likes_count } = res.data.data;
      set((state) => ({
        comments: state.comments.map((c) => {
          if (c.id === commentId) {
            return { ...c, liked_by_me: liked, likes_count };
          }
          // check replies
          const updatedReplies = c.replies.map((r) =>
            r.id === commentId ? { ...r, liked_by_me: liked, likes_count } : r
          );
          return { ...c, replies: updatedReplies };
        }),
      }));
    } catch {}
  },

  toggleResolve: async (commentId) => {
    try {
      const res = await apiClient.post(`/canvas-comments/${commentId}/resolve`);
      const { is_resolved } = res.data.data;
      set((state) => ({
        comments: state.comments.map((c) =>
          c.id === commentId ? { ...c, is_resolved } : c
        ),
      }));
    } catch {}
  },

  deleteComment: async (commentId) => {
    try {
      await apiClient.delete(`/canvas-comments/${commentId}`);
      set((state) => {
        // Could be top-level or a reply
        const filtered = state.comments.filter((c) => c.id !== commentId);
        const withReplyRemoved = filtered.map((c) => ({
          ...c,
          replies: c.replies.filter((r) => r.id !== commentId),
          replies_count: c.replies.filter((r) => r.id !== commentId).length,
        }));
        return { comments: withReplyRemoved };
      });
    } catch {}
  },

  openDrawer: (commentId) => set({ isDrawerOpen: true, activeCommentId: commentId }),
  closeDrawer: () => set({ isDrawerOpen: false, activeCommentId: null, pendingPin: null }),
  setPendingPin: (pin) => set({ pendingPin: pin }),
  setActiveCommentId: (id) => set({ activeCommentId: id }),
  reset: () => set({ comments: [], isLoading: false, isSaving: false, activeCommentId: null, isDrawerOpen: false, pendingPin: null }),
}));

export default useCanvasCommentsStore;
